import React, { useMemo } from 'react';
import { FilterInput } from '@tanium/react-filter-input';
import { ResultsHeaderControls } from '@tanium/react-results-header';
import { Stack, StackChild } from '@tanium/react-stack';
import { useTranslation } from '../../localization';
import { Id } from '../../types/Id';
import { RibbonListResultsHeaderProps } from '../../types/StackedRibbonListComponentTypes';
import { validateResultsCountsProps } from '../../utils/utils';

const isBefore = (listActionsPlacement: string | undefined): boolean =>
  listActionsPlacement === 'beforeFilterInput';

/**
 * A header containing results-oriented controls and information to place above a
 * StackedRibbonListMinimal.
 */
const RibbonListResultsHeaderInner = <TId extends Id, TItem>({
  defaultFilterText,
  filterPlaceholder,
  filterText,
  itemCount,
  itemsLabel,
  listActions: listActionsProp,
  onFilterTextChange,
  rowActions,
  selectedWellBg,
  selection,
  selectionMode,
  totalItemCount,
  listActionsPlacement,
}: RibbonListResultsHeaderProps<TId, TItem>) => {
  const { selectedCount } = validateResultsCountsProps({
    itemCount,
    totalItemCount,
    selection,
    selectionMode,
  });

  const listActions = useMemo(
    () => (listActionsProp ? React.Children.toArray(listActionsProp) : undefined),
    [listActionsProp],
  );

  const listActionsWrapper = !!listActions?.length && (
    <StackChild component={Stack} itemSpacing={1}>
      {listActions}
    </StackChild>
  );
  const { t } = useTranslation();

  return (
    <Stack justifyContent="space-between">
      <ResultsHeaderControls
        itemsLabel={itemsLabel}
        rowCount={itemCount}
        totalRowCount={totalItemCount}
        selectedRowCount={selectedCount}
        rowActions={rowActions}
        selectedWellBg={selectedWellBg}
      />
      <StackChild
        component={Stack}
        alignSelf="flex-start"
        itemSpacing={2}
        alignItems="center"
        minHeight={4}
      >
        {isBefore(listActionsPlacement) && listActionsWrapper}
        {onFilterTextChange && (
          <FilterInput
            size="sm"
            placeholder={filterPlaceholder ?? t('Filter Items')}
            onChange={onFilterTextChange}
            value={filterText}
            defaultValue={defaultFilterText}
          />
        )}
        {!isBefore(listActionsPlacement) && listActionsWrapper}
      </StackChild>
    </Stack>
  );
};

export const RibbonListResultsHeader = React.memo(RibbonListResultsHeaderInner) as <
  TId extends Id,
  TItem,
>(
  props: RibbonListResultsHeaderProps<TId, TItem>,
) => JSX.Element;
